import React from "react";
import clsx from "clsx";
import { useTheme } from "../themeConfig";
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: React.ReactNode;
  variant?: "normal" | "outline" | "inline";
  color?: string; // Tailwind color name like 'green', 'blue', 'red', etc.
  size?: "sm" | "md" | "lg";
  dir?: "rtl" | "ltr";
  icon?: React.ReactNode;
  "data-testid"?: string;
}
export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  variant = "normal",
  color = "blue",
  size = "md",
  dir,
  icon,
  "data-testid": testId,
  type = "button", 
  ...props
}) => {
  const {theme} = useTheme();
  const effectiveDir = dir || theme.direction;
  
  const getVariantClasses = () => {
    switch (variant) {
      case "normal":
        return `bg-${color}-500 hover:bg-${color}-600 text-white`;
      case "outline":
        const outlineColor = color === "gray" ? "gray" : color;
        return `border-2 border-${outlineColor}-500 text-${outlineColor}-500 hover:bg-${outlineColor}-50 bg-transparent`;
      case "inline":
        return `text-${color}-500 hover:text-${color}-600 bg-transparent hover:bg-transparent shadow-none`;
      default:
        return `bg-${color}-500 hover:bg-${color}-600 text-white`;
    }
  };
  const sizeClasses = {
    sm: "h-9 px-3 py-1 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-5 py-3 text-lg",
  };
  return (
    <button
      dir={effectiveDir} 
      aria-label={typeof children === "string" ? children : undefined}
      data-testid={testId}
      className={clsx(
        "rounded-lg focus:outline-none focus:ring-2 transition-colors font-medium whitespace-nowrap flex items-center gap-1",
        variant !== "inline" && "shadow",
        getVariantClasses(),
        `focus:ring-${color}-400 focus:ring-opacity-50`,
        sizeClasses[size],
        effectiveDir === "rtl" ? "text-right" : "text-left",
        className
      )}
      style={{
      fontFamily:
        effectiveDir === "rtl"
          ? theme.typography.fontFamily.hebrew
          : theme.typography.fontFamily.latin,
    }}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children} 
    </button>
  );
};
